import React, { createContext, useContext, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Demanda } from '../models';

interface DemandContextType {
  // Dados da demanda atual
  currentDemand: Demanda | null;
  loading: boolean;
  error: string | null;
  
  // Navegação
  currentIndex: number;
  totalDemandas: number;
  canNavigateNext: boolean;
  canNavigatePrevious: boolean;
  
  // Funções
  setDemandData: (data: {
    demanda: Demanda | null;
    loading: boolean;
    error: string | null;
    currentIndex: number;
    totalDemandas: number;
    canNavigateNext: boolean;
    canNavigatePrevious: boolean;
  }) => void;
  
  // Funções de navegação
  onNavigateNext?: (() => void) | undefined;
  onNavigatePrevious?: (() => void) | undefined;
  onGoBack?: (() => void) | undefined;
  onEditToggle?: (() => void) | undefined;
  
  setNavigationCallbacks: (callbacks: {
    onNavigateNext: () => void;
    onNavigatePrevious: () => void;
    onGoBack: () => void;
    onEditToggle?: () => void;
  }) => void;
  
  clearDemand: () => void;
}

const DemandContext = createContext<DemandContextType | undefined>(undefined);

interface DemandProviderProps {
  children: ReactNode;
}

export const DemandProvider: React.FC<DemandProviderProps> = ({ children }) => {
  const [currentDemand, setCurrentDemand] = useState<Demanda | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [totalDemandas, setTotalDemandas] = useState<number>(0);
  const [canNavigateNext, setCanNavigateNext] = useState<boolean>(false);
  const [canNavigatePrevious, setCanNavigatePrevious] = useState<boolean>(false);
  
  // Callbacks de navegação usando useRef para manter as funções atualizadas
  const onNavigateNextRef = useRef<(() => void) | undefined>(undefined);
  const onNavigatePreviousRef = useRef<(() => void) | undefined>(undefined);
  const onGoBackRef = useRef<(() => void) | undefined>(undefined);
  const onEditToggleRef = useRef<(() => void) | undefined>(undefined);

  const setDemandData = (data: {
    demanda: Demanda | null;
    loading: boolean;
    error: string | null;
    currentIndex: number;
    totalDemandas: number;
    canNavigateNext: boolean;
    canNavigatePrevious: boolean;
  }) => {
    setCurrentDemand(data.demanda);
    setLoading(data.loading);
    setError(data.error);
    setCurrentIndex(data.currentIndex);
    setTotalDemandas(data.totalDemandas);
    setCanNavigateNext(data.canNavigateNext);
    setCanNavigatePrevious(data.canNavigatePrevious);
  };

  const setNavigationCallbacks = (callbacks: {
    onNavigateNext: () => void;
    onNavigatePrevious: () => void;
    onGoBack: () => void;
    onEditToggle?: () => void;
  }) => {
    onNavigateNextRef.current = callbacks.onNavigateNext;
    onNavigatePreviousRef.current = callbacks.onNavigatePrevious;
    onGoBackRef.current = callbacks.onGoBack;
    if (callbacks.onEditToggle) {
      onEditToggleRef.current = callbacks.onEditToggle;
    }
  };

  const clearDemand = () => {
    setCurrentDemand(null);
    setLoading(false);
    setError(null);
    setCurrentIndex(-1);
    setTotalDemandas(0);
    setCanNavigateNext(false);
    setCanNavigatePrevious(false);
    onNavigateNextRef.current = undefined;
    onNavigatePreviousRef.current = undefined;
    onGoBackRef.current = undefined;
    onEditToggleRef.current = undefined;
  };

  const value: DemandContextType = {
    currentDemand,
    loading,
    error,
    currentIndex,
    totalDemandas,
    canNavigateNext,
    canNavigatePrevious,
    setDemandData,
    onNavigateNext: onNavigateNextRef.current,
    onNavigatePrevious: onNavigatePreviousRef.current,
    onGoBack: onGoBackRef.current,
    onEditToggle: onEditToggleRef.current,
    setNavigationCallbacks,
    clearDemand,
  };

  return (
    <DemandContext.Provider value={value}>
      {children}
    </DemandContext.Provider>
  );
};

export const useDemand = (): DemandContextType => {
  const context = useContext(DemandContext);
  if (context === undefined) {
    throw new Error('useDemand must be used within a DemandProvider');
  }
  return context;
};
